// Imperial⇄Metric unit-system toggle.
// All calculator INTERNAL state stays in imperial (ft, in, sq ft, cu ft, cu yd,
// gal, lb) so the math in src/calculators/*.ts never has to change. The display
// layer — <SmartField> on input, <ResultSheet> on output — converts to/from
// metric on the fly using the user's chosen system.
//
// Defaults: Canadian visitors (detected via region) get metric; everyone else
// gets imperial. Manual override is persisted in localStorage and survives
// across visits + tabs.
import { useEffect, useState } from 'react'
import { detectRegion } from './region'

export type UnitSystem = 'imperial' | 'metric'

const STORAGE_KEY = 'rs.unitSystem'
const EVENT_NAME = 'rs-unit-system'

// NOTE: the global Imperial/Metric toggle UI was removed from the header. With
// no way for a user to switch, the global system must default to 'imperial' so
// per-calculator unit choices (e.g. Paint's Gallons/Litres segmented, the unit
// the calc's compute function emits) aren't silently overridden by auto-
// detected region. Previously this returned 'metric' for CA visitors, which
// converted the user's chosen "Gallons" output to litres in ResultSheet. The
// helper functions stay in case the toggle is reintroduced later, but they
// always return 'imperial' for now — effectively a passthrough.
function defaultSystem(): UnitSystem {
  return 'imperial'
}

export function getUnitSystem(): UnitSystem {
  return 'imperial'
}

export function setUnitSystem(s: UnitSystem) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, s)
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: s }))
}

export function useUnitSystem(): [UnitSystem, (s: UnitSystem) => void] {
  // SSR-safe: render with 'imperial' on the server + first client paint, then
  // sync from localStorage after mount. Triggers one re-render on hydration —
  // acceptable for a non-LCP control.
  const [system, setSystemState] = useState<UnitSystem>('imperial')
  useEffect(() => {
    setSystemState(getUnitSystem())
    const handler = (e: Event) => setSystemState((e as CustomEvent).detail as UnitSystem)
    window.addEventListener(EVENT_NAME, handler)
    return () => window.removeEventListener(EVENT_NAME, handler)
  }, [])
  const update = (s: UnitSystem) => {
    setUnitSystem(s)
    setSystemState(s)
  }
  return [system, update]
}

// ─── Exact conversion factors ────────────────────────────────────────────────
// 1 in is defined as exactly 25.4 mm since 1959.
export const FT_TO_M = 0.3048
export const M_TO_FT = 1 / FT_TO_M
export const IN_TO_CM = 2.54
export const CM_TO_IN = 1 / IN_TO_CM
export const SQFT_TO_M2 = FT_TO_M * FT_TO_M           // ≈ 0.092903
export const M2_TO_SQFT = 1 / SQFT_TO_M2
export const CUFT_TO_M3 = FT_TO_M ** 3                // ≈ 0.0283168
export const M3_TO_CUFT = 1 / CUFT_TO_M3
export const CUYD_TO_M3 = 0.9144 ** 3                 // ≈ 0.764555
export const M3_TO_CUYD = 1 / CUYD_TO_M3
export const GAL_TO_L = 3.785411784                   // exact, US gallon since 1893
export const L_TO_GAL = 1 / GAL_TO_L
export const LB_TO_KG = 0.45359237                    // exact since 1959
export const KG_TO_LB = 1 / LB_TO_KG

// Canonical imperial unit identifiers used across calculators. Anything not in
// this list (e.g. 'bag', 'roll', 'piece') passes through unchanged — those are
// discrete counts, not measurements.
export type ImperialUnit = 'ft' | 'in' | 'yd' | 'sq ft' | 'sq yd' | 'cu ft' | 'cu yd' | 'gal' | 'gallon' | 'lb' | 'ton'

const IMPERIAL_UNITS = new Set<string>(['ft', 'in', 'yd', 'sq ft', 'sq yd', 'cu ft', 'cu yd', 'gal', 'gallon', 'lb', 'ton'])

export function isImperialMeasurement(unit: string): boolean {
  return IMPERIAL_UNITS.has(unit)
}

export interface ConvertedQty {
  qty: number
  unit: string
}

/** Take an imperial qty + unit and return the qty + unit in the user's system. */
export function convertQty(qty: number, imperialUnit: string, system: UnitSystem): ConvertedQty {
  if (system === 'imperial') return { qty, unit: imperialUnit }
  switch (imperialUnit) {
    case 'ft':    return { qty: qty * FT_TO_M, unit: 'm' }
    case 'in':    return { qty: qty * IN_TO_CM, unit: 'cm' }
    case 'yd':    return { qty: qty * 0.9144, unit: 'm' }
    case 'sq ft': return { qty: qty * SQFT_TO_M2, unit: 'm²' }
    case 'sq yd': return { qty: qty * 0.83612736, unit: 'm²' }
    case 'cu ft': return { qty: qty * CUFT_TO_M3, unit: 'm³' }
    case 'cu yd': return { qty: qty * CUYD_TO_M3, unit: 'm³' }
    case 'gal':
    case 'gallon': return { qty: qty * GAL_TO_L, unit: 'L' }
    case 'lb':    return { qty: qty * LB_TO_KG, unit: 'kg' }
    case 'ton':   return { qty: qty * 0.907185, unit: 't' } // short ton → metric tonne
    default:      return { qty, unit: imperialUnit }
  }
}

/** Reverse: take a metric value + the canonical imperial unit, return imperial value. */
export function toImperial(displayedValue: number, imperialUnit: string, system: UnitSystem): number {
  if (system === 'imperial') return displayedValue
  switch (imperialUnit) {
    case 'ft':    return displayedValue * M_TO_FT
    case 'in':    return displayedValue * CM_TO_IN
    case 'yd':    return displayedValue / 0.9144
    case 'sq ft': return displayedValue * M2_TO_SQFT
    case 'sq yd': return displayedValue / 0.83612736
    case 'cu ft': return displayedValue * M3_TO_CUFT
    case 'cu yd': return displayedValue * M3_TO_CUYD
    case 'gal':
    case 'gallon': return displayedValue * L_TO_GAL
    case 'lb':    return displayedValue * KG_TO_LB
    case 'ton':   return displayedValue / 0.907185
    default:      return displayedValue
  }
}

/** From imperial (stored) → displayed value in the current system. */
export function fromImperial(imperialValue: number, imperialUnit: string, system: UnitSystem): number {
  return convertQty(imperialValue, imperialUnit, system).qty
}

/** The unit label shown to the user (e.g. 'ft' or 'm') for a given imperial unit. */
export function displayUnit(imperialUnit: string, system: UnitSystem): string {
  return convertQty(1, imperialUnit, system).unit
}

/** Sensible step size for the displayed unit (e.g. 0.5 ft → 0.1 m; 1 in → 1 cm). */
export function displayStep(imperialStep: number, imperialUnit: string, system: UnitSystem): number {
  if (system === 'imperial') return imperialStep
  switch (imperialUnit) {
    case 'ft':    return 0.1            // 10 cm increment feels natural in meters
    case 'in':    return 1              // 1 cm increment for inch-scale measurements
    case 'yd':    return 0.1
    case 'sq ft': return 0.5
    case 'sq yd': return 0.5
    case 'cu ft': return 0.05
    case 'cu yd': return 0.05
    case 'gal':
    case 'gallon': return 0.5           // 0.5 L increments
    case 'lb':    return 0.5            // 0.5 kg
    default:      return imperialStep
  }
}
